import type {OwayoKonfiPart} from "./owayoPartWithSelObs";
import {PartSelectableObject} from "./partSelOb";
import {CONSTANTS} from "./meta_data";
import {Vector2} from "./delme_globals";
import {rightCurry} from "../global/functionalTools";
import {arrayIndexByKeyValue} from "../global/arrayHelper";

export class HandlePositions {

    public static readonly HANDLER_PADDING = 32;

    public static readonly zOrderDownHandle =
        {
            xBase   : "translationX",
            xOffset : - HandlePositions.HANDLER_PADDING -2,
            yBase   : "maxY",
            yOffset : HandlePositions.HANDLER_PADDING
        };
    public static readonly zOrderUpHandle =
        {
            xBase   : "translationX",
            xOffset : HandlePositions.HANDLER_PADDING + 2,
            yBase   : "maxY",
            yOffset : HandlePositions.HANDLER_PADDING
        };
    public static readonly deleteHandle =
        {
            xBase   : "minX",
            xOffset : - HandlePositions.HANDLER_PADDING,
            yBase   : "minY",
            yOffset : - HandlePositions.HANDLER_PADDING
        };
    public static readonly scalingHandle =
        {
            xBase   : "maxX",
            xOffset : HandlePositions.HANDLER_PADDING,
            yBase   : "minY",
            yOffset : - HandlePositions.HANDLER_PADDING
        };
    public static readonly rotateHandle =
        {
            xBase   : "maxX",
            xOffset : HandlePositions.HANDLER_PADDING,
            yBase   : "maxY",
            yOffset : HandlePositions.HANDLER_PADDING
        };
    public static readonly pinHandle =
        {
            xBase   : "minX",
            xOffset : - HandlePositions.HANDLER_PADDING,
            yBase   : "maxY",
            yOffset : HandlePositions.HANDLER_PADDING
        };
    public static readonly doubleHandle =
        {
            xBase   : "translationX",
            xOffset : 0,
            yBase   : "minY",
            yOffset : - HandlePositions.HANDLER_PADDING
        }
}


export class InteractionHandle{
    readonly type = "handle";
    handleFor = "";
    readonly id:string;
    readonly iconIndizes:{standard};
    status:string;
    shouldBeVisible = false;
    isVisible = false;
    translation:{x:number, y:number, xBase:string, xOffset:number, yBase:string, yOffset:number};
    covers;
    parent:OwayoKonfiPart|null = null;

    constructor(id:string, iconIndizes, offsetData:{xBase, xOffset, yBase, yOffset}, currentStatus="standard") {
        this.id=id;
        this.iconIndizes = typeof(iconIndizes) === "number" ? {standard: iconIndizes} : iconIndizes;
        this.status = currentStatus;

        this.translation    = {
            x       : 0,
            y       : 0,
            xBase   : offsetData.xBase,
            xOffset : offsetData.xOffset,
            yBase   : offsetData.yBase,
            yOffset : offsetData.yOffset
        };

        this.covers = function(v:Vector2){

            const PixelPositionInSelectableObject = new Vector2(0, 0);
            const extraHitSpace = (this.parent as OwayoKonfiPart).parent.mouseController.mouseInfo.isTouch ? CONSTANTS.HANDLER_WIDTH / 2.0 : 0;

            PixelPositionInSelectableObject.x = v.x - this.translation.x;
            PixelPositionInSelectableObject.y = v.y - this.translation.y;

            PixelPositionInSelectableObject.x = PixelPositionInSelectableObject.x + ( CONSTANTS.HANDLER_WIDTH / 2.0 );
            PixelPositionInSelectableObject.y = PixelPositionInSelectableObject.y + ( CONSTANTS.HANDLER_HEIGHT / 2.0 );

            // Wenn es größer 0 und kleiner Size ist so nehmen wir den Pixel von diesem SelectableObject.
            return (PixelPositionInSelectableObject.x >= - extraHitSpace
            && PixelPositionInSelectableObject.y >= -extraHitSpace
            && PixelPositionInSelectableObject.x <= CONSTANTS.HANDLER_WIDTH + extraHitSpace
            && PixelPositionInSelectableObject.y <= CONSTANTS.HANDLER_HEIGHT + extraHitSpace);

        };

    }
    setTranslationFromExtremes(extremes) {
        this.translation.x = extremes[this.translation.xBase] + this.translation.xOffset * (this.parent as OwayoKonfiPart).partStauchungsFaktor;
        this.translation.y = extremes[this.translation.yBase] + this.translation.yOffset;
    }

    /**
     * Setzt das "isVisible" Flag und fügt das InteractionHandle in insertTarget ein bzw. löscht es aus diesem Array.
     * @param {PartSelectableObject[]} insertTarget
     * @return {boolean}       gibt an, ob ein neuer Shader erstellt werden muss, da ein Handle hinzugefügt oder gelöscht wurde. FIXME CodeSmell Setter mit return wert, ist mal was für ne PairProgramming Session
     */
    setVisibilityAndAddToTarget(insertTarget) {
        if (this.shouldBeVisible === this.isVisible) {
            return false;
        }

        if (this.shouldBeVisible) {
            this.isVisible = true;
            insertTarget.push(this);
            return true;
        }
        this.isVisible = false;
        const index = arrayIndexByKeyValue(insertTarget, "id", this.id);
        insertTarget.splice(index,1);
        return true;
    }



    public static getInteractionHandles(parent:PartSelectableObject): HandleContainer{
       return new HandleContainer(parent);
    }


}
export class HandleContainer {
    public pinHandle: InteractionHandle;
    public rotateHandle: InteractionHandle;
    public scalingHandle: InteractionHandle;
    public deleteHandle: InteractionHandle;
    public zOrderUpHandle: InteractionHandle;
    public zOrderDownHandle: InteractionHandle;
    public doubleHandle: InteractionHandle;

    public constructor(parent:PartSelectableObject) {

        const setP = rightCurry(HandleContainer.setParent, parent);
        this.pinHandle = setP(HandleContainer.getPinHandle());
        this.rotateHandle = setP(HandleContainer.getRotateHandler());
        this.scalingHandle = setP(HandleContainer.getScalingHandle());
        this.deleteHandle = setP(HandleContainer.getDeleteHandle());
        this.zOrderUpHandle = setP(HandleContainer.getZOrderUpHandle());
        this.zOrderDownHandle = setP(HandleContainer.getZOrderDownHandle());
        this.doubleHandle = setP(HandleContainer.getDoubleHandle());
    }

    private static getPinHandle() {
        return new InteractionHandle(
            "pinHandle",
            {notPinned: 1,pinned: 2},
            HandlePositions.pinHandle,
            "notPinned"
        );
    }

    private static setParent(handle:InteractionHandle, parentPartSelOb:PartSelectableObject) {
        handle.handleFor = parentPartSelOb.id;
        handle.parent = parentPartSelOb.getParent();
        return handle;
    }

    private static getRotateHandler = ()=>new InteractionHandle("rotateHandle",4,HandlePositions.rotateHandle);
    private static getScalingHandle = ()=>new InteractionHandle("scalingHandle",3,HandlePositions.scalingHandle);
    private static getDeleteHandle = ()=>new InteractionHandle("deleteHandle",0,HandlePositions.deleteHandle);
    private static getZOrderUpHandle = ()=>new InteractionHandle("zOrderUpHandle",6,HandlePositions.zOrderUpHandle);
    private static getZOrderDownHandle = ()=>new InteractionHandle("zOrderDownHandle",5,HandlePositions.zOrderDownHandle);
    private static getDoubleHandle = ()=>new InteractionHandle("doubleHandle",7,HandlePositions.doubleHandle);
}

